import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import PubNub from 'pubnub';
import styled, { keyframes } from 'styled-components';
import { FiCheckCircle, FiUsers } from 'react-icons/fi';
import {
  loadDoctorState,
  loadOpsState,
  changeDoctorStatus,
  doStartCall,
  doClaimCase,
  applyOpsUpdate,
} from '../../actions/iciciQueue';
import { setActiveConsult } from '../../actions';
import config from '../../config';

const COLORS = {
  bg: '#0f1117',
  surface: '#1a1d26',
  border: '#2a2d3a',
  purple: '#8852cc',
  green: '#22c55e',
  yellow: '#f59e0b',
  red: '#ef4444',
  textPrimary: '#f1f5f9',
  textMuted: '#94a3b8',
};

const STATUS_META = {
  AVAILABLE: { color: COLORS.green,     label: 'Available' },
  IN_CALL:   { color: COLORS.yellow,    label: 'In Call'   },
  WRAP_UP:   { color: COLORS.purple,    label: 'Wrap-Up'   },
  OFFLINE:   { color: COLORS.textMuted, label: 'Offline'   },
};

const fadeIn = keyframes`from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; }`;
const ripple = keyframes`
  0%   { box-shadow: 0 0 0 0 ${COLORS.green}44; }
  100% { box-shadow: 0 0 0 16px transparent; }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9000;
`;

const Panel = styled.div`
  background: ${COLORS.bg};
  border: 1px solid ${COLORS.border};
  border-radius: 1.2rem;
  width: 44rem;
  max-width: 95vw;
  max-height: 90vh;
  overflow-y: auto;
  padding: 2.4rem;
  animation: ${fadeIn} 0.25s ease;
  position: relative;
  color: ${COLORS.textPrimary};
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
  padding-bottom: 1.2rem;
  border-bottom: 1px solid ${COLORS.border};
`;

const PanelTitle = styled.div`
  font-size: 1.4rem;
  font-weight: 600;
  color: ${COLORS.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const CloseBtn = styled.button`
  background: transparent;
  border: 1px solid ${COLORS.border};
  color: ${COLORS.textMuted};
  border-radius: 0.5rem;
  width: 3rem;
  height: 3rem;
  font-size: 1.6rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { border-color: ${COLORS.red}; color: ${COLORS.red}; }
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
`;

const StatusDot = styled.div`
  width: 1.1rem;
  height: 1.1rem;
  border-radius: 50%;
  background: ${(p) => p.color};
  animation: ${(p) => p.pulse ? ripple : 'none'} 1.8s infinite;
  flex-shrink: 0;
`;

const StatusLabel = styled.span`
  font-size: 1.8rem;
  font-weight: 700;
  color: ${(p) => p.color};
`;

const DoctorName = styled.div`
  font-size: 1.3rem;
  color: ${COLORS.textMuted};
`;

const ToggleGroup = styled.div`
  display: flex;
  gap: 0.6rem;
  margin-left: auto;
`;

const ToggleBtn = styled.button`
  padding: 0.45rem 1rem;
  border-radius: 0.5rem;
  font-size: 1.2rem;
  font-weight: 600;
  border: 1.5px solid ${(p) => p.active ? p.activeColor : COLORS.border};
  background: ${(p) => p.active ? (p.activeColor + '22') : 'transparent'};
  color: ${(p) => p.active ? p.activeColor : COLORS.textMuted};
  cursor: pointer;
  transition: all 0.15s;
  &:hover { border-color: ${(p) => p.activeColor}; color: ${(p) => p.activeColor}; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const CaseCard = styled.div`
  background: ${COLORS.surface};
  border: 1.5px solid ${COLORS.border};
  border-radius: 1rem;
  padding: 1.6rem;
  margin-bottom: 1.6rem;
`;

const CaseName = styled.div`font-size: 1.8rem; font-weight: 700; margin-bottom: 0.4rem;`;

const CaseMeta = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.6rem 1.4rem;
  margin-top: 1rem;
`;

const MetaLabel = styled.div`font-size: 1.1rem; color: ${COLORS.textMuted};`;
const MetaValue = styled.div`font-size: 1.3rem; font-weight: 600;`;

const WaitBadge = styled.div`
  display: inline-flex;
  align-items: center;
  font-size: 1.2rem;
  font-weight: 600;
  color: ${COLORS.yellow};
  background: ${COLORS.yellow}22;
  padding: 0.25rem 0.7rem;
  border-radius: 0.4rem;
  margin-top: 0.6rem;
`;

const StartBtn = styled.button`
  width: 100%;
  padding: 1.3rem;
  background: ${COLORS.green};
  color: #fff;
  border: none;
  border-radius: 0.8rem;
  font-size: 1.5rem;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.15s;
  &:hover { opacity: 0.88; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const IdleBox = styled.div`
  text-align: center;
  padding: 3rem 1rem;
`;

const IdleIcon = styled.div`font-size: 3.2rem; margin-bottom: 0.8rem;`;
const IdleText = styled.div`font-size: 1.4rem; color: ${COLORS.textMuted};`;

const QueueStatusCard = styled.div`
  display: flex;
  align-items: center;
  gap: 1.2rem;
  padding: 1.4rem 1.6rem;
  margin-bottom: 1.6rem;
  background: ${(p) => p.tone === 'urgent' ? `${COLORS.red}10`
                  : p.tone === 'warm'   ? `${COLORS.yellow}10`
                                        : COLORS.surface};
  border: 1px solid ${(p) => p.tone === 'urgent' ? `${COLORS.red}55`
                          : p.tone === 'warm'   ? `${COLORS.yellow}44`
                                                : COLORS.border};
  border-radius: 0.9rem;
  transition: all 0.2s;
`;

const QueueIconBox = styled.div`
  width: 4rem;
  height: 4rem;
  border-radius: 0.7rem;
  background: ${(p) => (p.color || COLORS.green) + '22'};
  color: ${(p) => p.color || COLORS.green};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const QueueMain = styled.div`flex: 1; min-width: 0;`;

const QueueTitle = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${(p) => p.color || COLORS.textPrimary};
  letter-spacing: -0.01em;
  line-height: 1.2;
  span.suffix {
    font-size: 1.2rem;
    font-weight: 500;
    color: ${COLORS.textMuted};
    margin-left: 0.4rem;
  }
`;

const QueueDetail = styled.div`
  font-size: 1.15rem;
  color: ${COLORS.textMuted};
  margin-top: 0.3rem;
  strong {
    color: ${(p) => p.timeColor || COLORS.textPrimary};
    font-weight: 700;
  }
`;

const WaitingListWrap = styled.div`margin-bottom: 1.2rem;`;

const WaitingListLabel = styled.div`
  font-size: 1.05rem;
  font-weight: 700;
  color: ${COLORS.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.07em;
  margin-bottom: 0.7rem;
`;

const WaitingItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.9rem 1.1rem;
  background: ${COLORS.surface};
  border: 1px solid ${(p) => p.urgent ? `${COLORS.red}55`
                          : p.warning ? `${COLORS.yellow}44`
                                      : COLORS.border};
  border-radius: 0.7rem;
  margin-bottom: 0.6rem;
  &:last-child { margin-bottom: 0; }
`;

const WaitingAvatar = styled.div`
  width: 3rem; height: 3rem;
  border-radius: 50%;
  background: ${COLORS.purple}22;
  border: 1px solid ${COLORS.purple}55;
  color: ${COLORS.purple};
  display: flex; align-items: center; justify-content: center;
  font-size: 1.2rem; font-weight: 700;
  flex-shrink: 0;
`;

const WaitingInfo = styled.div`flex: 1; min-width: 0;`;

const WaitingName = styled.div`
  font-size: 1.25rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const WaitingMeta = styled.div`
  font-size: 1.05rem;
  color: ${COLORS.textMuted};
  margin-top: 0.15rem;
  font-family: 'SF Mono', 'Fira Code', monospace;
  letter-spacing: 0.02em;
`;

const WaitingTime = styled.div`
  font-size: 1.15rem;
  font-weight: 700;
  color: ${(p) => p.urgent ? COLORS.red : p.warning ? COLORS.yellow : COLORS.green};
  flex-shrink: 0;
`;

const TakeBtn = styled.button`
  background: ${COLORS.green};
  color: #fff;
  border: none;
  border-radius: 0.5rem;
  padding: 0.55rem 1.1rem;
  font-size: 1.15rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s;
  flex-shrink: 0;
  &:hover:not(:disabled) {
    background: ${COLORS.green}dd;
    box-shadow: 0 0 0 3px ${COLORS.green}33;
  }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const OverflowNote = styled.div`
  text-align: center;
  font-size: 1.05rem;
  color: ${COLORS.textMuted};
  padding: 0.5rem;
`;

const NewCaseToast = styled.div`
  position: absolute;
  top: 1rem;
  left: 50%;
  transform: translateX(-50%);
  background: ${COLORS.surface};
  border: 1.5px solid ${COLORS.purple};
  border-radius: 0.8rem;
  padding: 1rem 1.6rem;
  min-width: 24rem;
  box-shadow: 0 4px 16px rgba(0,0,0,0.4);
  animation: ${fadeIn} 0.25s ease;
  z-index: 1;
`;

const ToastTitle = styled.div`font-size: 1.2rem; font-weight: 700; color: ${COLORS.purple}; margin-bottom: 0.4rem;`;
const ToastRow = styled.div`font-size: 1.1rem; color: ${COLORS.textMuted}; span { color: ${COLORS.textPrimary}; font-weight: 600; }`;

const ErrorText = styled.div`
  font-size: 1.2rem;
  color: ${COLORS.red};
  text-align: center;
  margin-top: 0.8rem;
`;

const fmtSeconds = (s) => {
  if (!s && s !== 0) return '—';
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
};

function IciciInstantPanel(props) {
  const {
    doctorId, doctorFirstName, doctorLastName,
    doctorStatus, assignedCase, queue, onClose,
  } = props;

  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState(null);
  const [toast, setToast] = useState(null);
  const [, setTick] = useState(0);
  const [claimingId, setClaimingId] = useState(null);
  const [claimError, setClaimError] = useState(null);
  const pubnubRef = useRef(null);
  const toastTimerRef = useRef(null);

  useEffect(() => {
    props.loadDoctorState();
    props.loadOpsState();
    const t = setInterval(() => setTick((v) => v + 1), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!doctorId) return;
    setupPubNub(doctorId);
    return () => {
      if (pubnubRef.current) pubnubRef.current.unsubscribeAll();
    };
  }, [doctorId]);

  const setupPubNub = (id) => {
    if (pubnubRef.current) pubnubRef.current.unsubscribeAll();
    const pn = new PubNub({
      publishKey: config.pubnubPublishKey,
      subscribeKey: config.pubnubSubscribeKey,
      uuid: `doctor-panel-${id}`,
    });
    pn.addListener({
      message: ({ message }) => {
        if (message.name === 'new_case_inbound') {
          props.loadDoctorState();
          showToast({
            customerName: message.customerName,
            applicationId: message.applicationId,
            waitSeconds: message.waitSeconds,
          });
        }
        if (message.name === 'ops_queue_update') {
          props.applyOpsUpdate(message);
        }
      },
    });
    pn.subscribe({ channels: [`icici-instant-doctor-${id}`, 'icici-instant-ops'] });
    pubnubRef.current = pn;
  };

  const showToast = (data) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast(data);
    toastTimerRef.current = setTimeout(() => setToast(null), 7000);
  };

  const handleClaim = async (caseRow) => {
    if (claimingId) return;
    setClaimingId(caseRow.consultationId);
    setClaimError(null);
    try {
      const result = await props.dispatch(doClaimCase(caseRow.consultationId));
      if (!result) {
        setClaimError('Something went wrong. Please try again.');
        setClaimingId(null);
        return;
      }
      if (result.status === 'already_taken') {
        setClaimError('That case was just taken by another doctor.');
        setClaimingId(null);
        return;
      }
      if (result.status === 'success') {
        props.dispatch(setActiveConsult({
          consultationId: caseRow.consultationId,
          callId: result.callId,
          user: {
            userId: caseRow.userId || result.userId || null,
            userName: caseRow.name,
            gender: caseRow.gender || '',
          },
          labels: [],
          oneConnectLinks: [],
          relatives: [],
          requestType: 'video',
          callType: 'video',
          channel: `icici-instant-${caseRow.consultationId}`,
        }));
        props.dispatch({
          type: 'INITIATE_VIDEO_CALL',
          payload: {
            isInsta: 1,
            instantConsultationId: caseRow.consultationId,
          },
        });
        onClose();
      }
    } catch (err) {
      console.error('[IciciInstantPanel handleClaim]', err);
      setClaimError('Something went wrong. Please try again.');
      setClaimingId(null);
    }
  };

  const handleStartCall = async () => {
    if (!assignedCase || starting) return;
    setStarting(true);
    setStartError(null);
    try {
      const result = await props.doStartCall(assignedCase.consultation_id);
      if (!result || result.status !== 'success') {
        setStartError(result && result.errorMessage ? result.errorMessage : 'Failed to start call');
        setStarting(false);
        return;
      }
      props.dispatch(setActiveConsult({
        consultationId: assignedCase.consultation_id,
        callId: result.callId,
        user: {
          userId: assignedCase.user_id,
          userName: assignedCase.name,
          gender: assignedCase.gender || '',
        },
        labels: [],
        oneConnectLinks: [],
        relatives: [],
        requestType: 'video',
        callType: 'video',
        channel: `icici-instant-${assignedCase.consultation_id}`,
      }));
      props.dispatch({
        type: 'INITIATE_VIDEO_CALL',
        payload: {
          isInsta: 1,
          instantConsultationId: assignedCase.consultation_id,
        },
      });
      onClose();
    } catch (err) {
      console.error('[IciciInstantPanel handleStartCall]', err);
      setStartError('Something went wrong. Please try again.');
    }
    setStarting(false);
  };

  const status = doctorStatus ? doctorStatus.status : 'OFFLINE';
  const statusMeta = STATUS_META[status] || STATUS_META.OFFLINE;

  const waitingCases = (queue || []).filter((c) => c.status === 'WAITING');
  const waitingCount = waitingCases.length;
  const longestWait = waitingCases.reduce((max, c) => {
    const elapsed = c.queuedAt
      ? Math.floor((Date.now() - new Date(c.queuedAt).getTime()) / 1000)
      : (c.elapsedSeconds || 0);
    return Math.max(max, elapsed);
  }, 0);
  const queueTone = waitingCount === 0 ? 'calm'
                  : longestWait > 180  ? 'urgent'
                                       : 'warm';
  const longestWaitColor = longestWait > 180 ? COLORS.red
                         : longestWait > 90  ? COLORS.yellow
                                             : COLORS.textPrimary;

  return (
    <Overlay onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <Panel>
        { false && toast && (
          <NewCaseToast>
            <ToastTitle>New Case Assigned</ToastTitle>
            <ToastRow>Customer: <span>{toast.customerName}</span></ToastRow>
            <ToastRow>App ID: <span>{toast.applicationId}</span></ToastRow>
            {toast.waitSeconds > 0 && (
              <ToastRow>Waited: <span>{fmtSeconds(toast.waitSeconds)}</span></ToastRow>
            )}
          </NewCaseToast>
        )}

        <PanelHeader>
          <PanelTitle>ICICI Instant vMER</PanelTitle>
          <CloseBtn onClick={onClose}>✕</CloseBtn>
        </PanelHeader>

        <StatusRow>
          <StatusDot color={statusMeta.color} pulse={status === 'AVAILABLE'} />
          <StatusLabel color={statusMeta.color}>{statusMeta.label}</StatusLabel>
          <DoctorName>Dr. {doctorFirstName} {doctorLastName}</DoctorName>
          <ToggleGroup>
            <ToggleBtn
              active={status === 'AVAILABLE'}
              activeColor={COLORS.green}
              onClick={() => props.changeDoctorStatus('AVAILABLE')}
              disabled={status === 'IN_CALL'}
            >
              Available
            </ToggleBtn>
            <ToggleBtn
              active={status === 'OFFLINE'}
              activeColor={COLORS.textMuted}
              onClick={() => props.changeDoctorStatus('OFFLINE')}
              disabled={status === 'IN_CALL'}
            >
              Offline
            </ToggleBtn>
          </ToggleGroup>
        </StatusRow>

        {status !== 'OFFLINE' && (
          <QueueStatusCard tone={queueTone}>
            {waitingCount === 0 ? (
              <>
                <QueueIconBox color={COLORS.green}>
                  <FiCheckCircle size={20} />
                </QueueIconBox>
                <QueueMain>
                  <QueueTitle color={COLORS.green}>Queue is clear</QueueTitle>
                  <QueueDetail>No customers waiting right now</QueueDetail>
                </QueueMain>
              </>
            ) : (
              <>
                <QueueIconBox color={queueTone === 'urgent' ? COLORS.red : COLORS.yellow}>
                  <FiUsers size={20} />
                </QueueIconBox>
                <QueueMain>
                  <QueueTitle color={queueTone === 'urgent' ? COLORS.red : COLORS.yellow}>
                    {waitingCount}
                    <span className="suffix">
                      customer{waitingCount > 1 ? 's' : ''} waiting in queue
                    </span>
                  </QueueTitle>
                  {longestWait > 0 && (
                    <QueueDetail timeColor={longestWaitColor}>
                      Longest wait: <strong>{fmtSeconds(longestWait)}</strong>
                    </QueueDetail>
                  )}
                </QueueMain>
              </>
            )}
          </QueueStatusCard>
        )}

        {assignedCase ? (
          <>
            <CaseCard>
              <CaseName>{assignedCase.name}</CaseName>
              {(assignedCase.waitSeconds || assignedCase.wait_seconds) > 0 && (
                <WaitBadge>
                  Waited {fmtSeconds(assignedCase.waitSeconds || assignedCase.wait_seconds)}
                </WaitBadge>
              )}
              <CaseMeta>
                <div>
                  <MetaLabel>Application ID</MetaLabel>
                  <MetaValue>{assignedCase.applicationId || '—'}</MetaValue>
                </div>
                <div>
                  <MetaLabel>Gender</MetaLabel>
                  <MetaValue>{assignedCase.gender || '—'}</MetaValue>
                </div>
                <div>
                  <MetaLabel>Phone</MetaLabel>
                  <MetaValue>{assignedCase.phone || '—'}</MetaValue>
                </div>
                <div>
                  <MetaLabel>Status</MetaLabel>
                  <MetaValue>{assignedCase.status}</MetaValue>
                </div>
              </CaseMeta>
            </CaseCard>
            <StartBtn onClick={handleStartCall} disabled={starting}>
              {starting ? 'Connecting…' : 'Start Call Now'}
            </StartBtn>
            {startError && <ErrorText>{startError}</ErrorText>}
          </>
        ) : status === 'AVAILABLE' && waitingCount > 0 ? (
          <WaitingListWrap>
            <WaitingListLabel>Available to Take</WaitingListLabel>
            {waitingCases.slice(0, 5).map((c) => {
              const elapsed = c.queuedAt
                ? Math.floor((Date.now() - new Date(c.queuedAt).getTime()) / 1000)
                : (c.elapsedSeconds || 0);
              const urgent  = elapsed > 180;
              const warning = elapsed > 90;
              const isClaimingThis = claimingId === c.consultationId;
              return (
                <WaitingItem key={c.consultationId} urgent={urgent} warning={warning}>
                  <WaitingAvatar>{(c.name || '?')[0].toUpperCase()}</WaitingAvatar>
                  <WaitingInfo>
                    <WaitingName>{c.name}</WaitingName>
                    <WaitingMeta>
                      {c.applicationId || '—'}{c.gender ? ` · ${c.gender}` : ''}
                    </WaitingMeta>
                  </WaitingInfo>
                  <WaitingTime urgent={urgent} warning={warning}>
                    {fmtSeconds(elapsed)}
                  </WaitingTime>
                  <TakeBtn onClick={() => handleClaim(c)} disabled={!!claimingId}>
                    {isClaimingThis ? 'Taking…' : 'Take'}
                  </TakeBtn>
                </WaitingItem>
              );
            })}
            {waitingCount > 5 && (
              <OverflowNote>+{waitingCount - 5} more in queue</OverflowNote>
            )}
            {claimError && <ErrorText>{claimError}</ErrorText>}
          </WaitingListWrap>
        ) : (
          <IdleBox>
            <IdleIcon>🩺</IdleIcon>
            {status === 'OFFLINE' ? (
              <IdleText>You are offline. Click Available to start receiving cases.</IdleText>
            ) : status === 'WRAP_UP' ? (
              <IdleText>Wrap-up in progress… You'll be auto-set to Available shortly.</IdleText>
            ) : (
              <IdleText>You're available — waiting for the next case.</IdleText>
            )}
          </IdleBox>
        )}
      </Panel>
    </Overlay>
  );
}

const mapStateToProps = (state) => ({
  doctorId:        state.user.doctorId,
  doctorFirstName: state.user.doctorFirstName,
  doctorLastName:  state.user.doctorLastName,
  doctorStatus:    state.iciciQueue.doctorStatus,
  assignedCase:    state.iciciQueue.assignedCase,
  queue:           state.iciciQueue.queue,
});

const mapDispatchToProps = (dispatch) => ({
  dispatch,
  loadDoctorState:    (...args) => dispatch(loadDoctorState(...args)),
  loadOpsState:       (...args) => dispatch(loadOpsState(...args)),
  changeDoctorStatus: (...args) => dispatch(changeDoctorStatus(...args)),
  doStartCall:        (...args) => dispatch(doStartCall(...args)),
  applyOpsUpdate:     (...args) => dispatch(applyOpsUpdate(...args)),
});

export default connect(mapStateToProps, mapDispatchToProps)(IciciInstantPanel);
